import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, apiError } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Eye, EyeSlash } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back");
      navigate("/");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-zinc-950">
      <div className="hidden lg:block w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1635335874521-7987db781153?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
          alt="Industrial electrical panel manufacturing"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-zinc-950/75" />
        <div className="absolute inset-0 tech-grid opacity-40" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <div className="flex items-center gap-2.5">
            <img src="/dvepl-logo.svg" alt="DVEPL logo" className="w-10 h-10 rounded-lg" data-testid="login-hero-logo" />
            <span className="font-heading font-black text-xl text-white tracking-tight">DVEPL</span>
          </div>
          <div>
            <h1 className="font-heading text-4xl xl:text-5xl font-black tracking-tighter text-white leading-tight">
              Workflow &<br />Team Task<br />
              <span className="text-[#3ecf82]">Management</span>
            </h1>
            <p className="text-[#cfe3d6] mt-4 max-w-md">
              Assign, track and deliver industrial panel work with deadlines, dependencies,
              mandatory feedback and full audit control.
            </p>
          </div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#9fb8a8] font-mono">
            Secure Role-Based Access • Central Management Dashboard
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <img src="/dvepl-logo.svg" alt="DVEPL logo" className="w-9 h-9 rounded-md" />
            <span className="font-heading font-black text-lg text-zinc-50">DVEPL</span>
          </div>
          <img src="/dvepl-logo.svg" alt="DVEPL" className="hidden lg:block w-12 h-12 rounded-lg mb-6" data-testid="login-form-logo" />
          <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-600 font-bold mb-2">Sign In</div>
          <h2 className="font-heading text-3xl font-black tracking-tight text-zinc-50 mb-8">
            Access your portal
          </h2>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1.5 block">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email"
                className="w-full bg-zinc-900 border border-zinc-800 rounded-sm px-3 py-2.5 text-sm text-zinc-100 focus:border-emerald-600 focus:outline-none focus:ring-1 focus:ring-emerald-600 transition-colors"
                placeholder="you@company.com"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1.5 block">Password</label>
              <div className="relative">
                <input
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password"
                  className="w-full bg-zinc-900 border border-zinc-800 rounded-sm px-3 py-2.5 pr-10 text-sm text-zinc-100 focus:border-emerald-600 focus:outline-none focus:ring-1 focus:ring-emerald-600 transition-colors"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  data-testid="toggle-password"
                  aria-label={showPw ? "Hide password" : "Show password"}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-emerald-400 transition-colors"
                >
                  {showPw ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div className="flex justify-end">
              <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs font-medium text-emerald-400 hover:text-emerald-300 transition-colors">
                Forgot password?
              </Link>
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-semibold h-11"
            >
              {loading ? "Signing in…" : "Sign In"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
